import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Calendar, MapPin, Crosshair, Lightning, CheckCircle } from '@phosphor-icons/react';
import { requestPermissionWithRationale } from './PermissionRationaleModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-700',
  present: 'bg-green-100 text-green-700',
  absent: 'bg-red-100 text-red-700',
  replaced: 'bg-gray-200 text-text-secondary',
};

const ProAttendanceWidget = () => {
  const [events, setEvents] = useState([]);
  const [replacements, setReplacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(null);

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    try {
      const [evRes, repRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/pro/events`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/pro/replacements`, { withCredentials: true }),
      ]);
      setEvents(evRes.data || []);
      setReplacements(repRes.data || []);
    } catch (err) {
      // silent — endpoint returns 403 for non-pro users
    }
    setLoading(false);
  };

  const checkIn = async (eventId) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }
    const granted = await requestPermissionWithRationale('location', { purpose: 'pro-check-in' });
    if (!granted) return;
    setCheckingIn(eventId);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await axios.post(
            `${BACKEND_URL}/api/pro/events/${eventId}/check-in`,
            { lat: pos.coords.latitude, lng: pos.coords.longitude },
            { withCredentials: true },
          );
          toast.success(`Checked in! ${data.distance_m}m from venue`);
          refresh();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Check-in failed');
        }
        setCheckingIn(null);
      },
      (err) => { toast.error(`Location error: ${err.message}`); setCheckingIn(null); },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const acceptReplacement = async (repId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/pro/replacements/${repId}/accept`,
        {},
        { withCredentials: true },
      );
      toast.success("You've accepted the replacement slot!");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not claim — already filled?');
      refresh();
    }
  };

  if (loading) return null;
  if (events.length === 0 && replacements.length === 0) return null;

  return (
    <div className="space-y-4 mt-6" data-testid="pro-attendance-widget">
      {/* Replacement pool — first-accept-wins */}
      {replacements.length > 0 && (
        <Card className="bg-amber-50/40 border-amber-300" data-testid="replacement-pool">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Lightning size={20} className="text-amber-600" weight="fill" /> Open Replacement Slots
            </CardTitle>
            <CardDescription>First to accept wins. Earn the slot before someone else does.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {replacements.map((r) => (
                <div key={r.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border border-amber-200 bg-white rounded" data-testid={`rep-${r.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{r.package_name || 'Event'} <span className="text-xs text-text-secondary">• {r.category}</span></div>
                    {r.event_location_text && <div className="text-xs text-text-secondary"><MapPin size={11} className="inline" /> {r.event_location_text}</div>}
                    {r.expected_check_in && <div className="text-xs text-text-secondary">Expected: {r.expected_check_in}</div>}
                  </div>
                  <Button onClick={() => acceptReplacement(r.id)} size="sm" className="rounded-none bg-amber-600 hover:bg-amber-700 text-white" data-testid={`accept-rep-${r.id}`}>
                    Accept Slot
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* My assigned events with check-in */}
      {events.length > 0 && (
        <Card className="bg-white border-[#E8E1D5]" data-testid="pro-events-list">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Calendar size={20} className="text-brand-primary" /> My Event Assignments
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {events.map((ev) => {
              const slot = ev.my_slot || {};
              const canCheckIn = slot.status === 'pending' && ev.venue;
              return (
                <div key={ev.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border border-[#E8E1D5] rounded" data-testid={`pro-event-${ev.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{ev.package_name || 'Event'} <span className="text-xs text-text-secondary">• {ev.event_date}</span></div>
                    <div className="text-xs text-text-secondary">Captain: {ev.captain_name || '—'}</div>
                    {ev.venue ? (
                      <div className="text-xs text-emerald-700">
                        <Crosshair size={11} className="inline" /> {ev.venue.place_name || 'Venue set'}
                      </div>
                    ) : (
                      <div className="text-xs text-amber-700">Captain hasn't set venue yet — wait for check-in to open</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${STATUS_BADGE[slot.status] || 'bg-gray-100 text-gray-700'}`}>{slot.status || '—'}</span>
                    {canCheckIn && (
                      <Button onClick={() => checkIn(ev.id)} disabled={checkingIn === ev.id} size="sm" className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50" data-testid={`check-in-${ev.id}`}>
                        <CheckCircle size={14} className="mr-1" /> {checkingIn === ev.id ? 'Checking…' : 'Check In'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProAttendanceWidget;
